import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, map, switchMap, take } from 'rxjs';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';

import { isEmbeddedInIframe, setMaxHeight } from '@app/utils/utils';
import { PopupsService } from '@voyagu/portal-design-system';
import { ErrorService } from '@app/services/error.service';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
import { passwordSalt } from '@app/utils/constants';
import { VerificationModalService } from '@app/services/verification-modal.service';
import { SignInModalService } from '@app/services/sign-in-modal.service';
import { CommonDataService } from '@app/services/common-data/common-data.service';
import { handleError } from '@app/operators/handle-error';
import {
  AuthorizedDataNode,
  PortalQuotePasscodeRole,
  PortalUserVerificationNode,
} from '@app/services/api/api.types';
import { ApiProtectedService } from '@app/services/api/api.protected.service';
import { AuthService } from '@app/services/auth/auth.service';
import { StateService } from '@app/services/state/state.service';
import { ApiService } from '@app/services/api/api.service';
import { CheckoutResumeService } from '@app/services/checkout-resume.service';
import { patchCheckoutStateFromUrl } from '@app/utils/checkout-resume.util';
import { AuthorizedDataService } from '@app/services/authorized-data/authorized-data.service';
import { PasscodeModalService } from '@app/services/passcode-modal.service';
import { PasscodeTokenService } from '@app/services/passcode-token.service';
import { PortalTierCatalogService } from '@app/services/portal-tier-catalog/portal-tier-catalog.service';

declare global {
  interface Window {
    track: (params: unknown) => void;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  cognitoHostedUIIdentityProvider = CognitoHostedUIIdentityProvider;

  @ViewChild('errorTemplate') errorTemplate: TemplateRef<unknown>;
  @ViewChild('signInTemplate') signInTemplate: TemplateRef<unknown>;
  @ViewChild('continueWithEmailTemplate') continueWithEmailTemplate: TemplateRef<unknown>;
  @ViewChild('activationCodeTemplate') activationCodeTemplate: TemplateRef<unknown>;
  @ViewChild('verificationTemplate') verificationTemplate: TemplateRef<unknown>;
  @ViewChild('passcodeTemplate') passcodeTemplate: TemplateRef<unknown>;

  router = inject(Router);
  destroyRef = inject(DestroyRef);
  errorService = inject(ErrorService);
  changeDetectorRef = inject(ChangeDetectorRef);
  apiProtectedService = inject(ApiProtectedService);
  signInModalService = inject(SignInModalService);
  commonDataService = inject(CommonDataService);
  popupsService = inject(PopupsService);
  stateService = inject(StateService);
  authService = inject(AuthService);
  apiService = inject(ApiService);
  verificationModalService = inject(VerificationModalService);
  authorizedDataService = inject(AuthorizedDataService);
  activatedRoute = inject(ActivatedRoute);
  checkoutResumeService = inject(CheckoutResumeService);
  passcodeModalService = inject(PasscodeModalService);
  passcodeTokenService = inject(PasscodeTokenService);
  portalTierCatalogService = inject(PortalTierCatalogService);

  activation: PortalUserVerificationNode;

  activationPassed = false;

  activationError = '';
  email = '';

  ngOnInit() {
    if (isEmbeddedInIframe()) {
      document.documentElement.classList.add('embedded');
    }

    patchCheckoutStateFromUrl(new URL(window.location.href), this.stateService);

    this.initSessionUser();

    this.portalTierCatalogService.catalog$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.errorService.commonError$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.showErrorModal());

    this.verificationModalService.verification$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.showVerificationModal());

    this.passcodeModalService.passcode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.showPasscodeModal());

    this.verificationModalService.activation$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ email, activation }) => {
        this.email = email;
        this.activation = activation;

        this.signInModalService.currentUrl = `${window.location.origin}/travelers`;

        this.showActivationCodeModal();
      });

    this.signInModalService.signInModal$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.showSignInModal());

    this.authorizedDataService.refetchAuthorizedData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .pipe(switchMap(() => this.authService.hasSession()))
      .pipe(
        switchMap((hasSession) => {
          if (hasSession) {
            return this.getAuthorizedData();
          }

          console.error('Invalid session: refetchAuthorizedData$');
          return EMPTY;
        }),
      )
      .subscribe();

    setMaxHeight();
    window.addEventListener('resize', setMaxHeight);
  }

  hideSignInModal() {
    this.hideModal();
    this.onHideSignInModal();
  }

  continueWith(provider: CognitoHostedUIIdentityProvider) {
    Tracking.track(
      provider === CognitoHostedUIIdentityProvider.Facebook
        ? PortalTrackingEvents.portalContinueWithFacebookPressed
        : PortalTrackingEvents.portalContinueWithGooglePressed,
    );

    void this.authService.federatedSignIn(provider, this.signInModalService.currentUrl);
  }

  continueWithEmail() {
    this.hideModal();
    this.popupsService.showModal(this.continueWithEmailTemplate);

    Tracking.track(PortalTrackingEvents.portalContinueWithEmailPressed);
  }

  sendEmail(email: string) {
    this.activationError = '';
    this.email = email;

    const portalLink = this.signInModalService.portalLink;

    const variables = { portalLink, email };

    this.apiService
      .sendPasswordToEmail(variables)
      .pipe(take(1))
      .subscribe(({ errors }) => {
        if (errors?.length) {
          this.activationError = errors[0].message;
        } else {
          this.hideModal();

          this.activation = {
            emailMasked: this.email,
            emailSentAt: new Date().toUTCString(),
          };

          this.showActivationCodeModal();
        }

        this.changeDetectorRef.detectChanges();
      });
  }

  validateNewEmailCode(password: string) {
    this.activationPassed = false;
    this.activationError = '';

    void this.authService
      .signIn(this.email, `${password}${atob(passwordSalt)}`)
      .pipe(
        take(1),
        map(() => {
          this.activationPassed = true;
          const url = this.signInModalService.currentUrl;

          setTimeout(() => {
            if (url) {
              window.location.href = url;
              return;
            }

            window.location.reload();
          }, 1000);

          this.changeDetectorRef.detectChanges();
        }),
        catchError((error: Error) => {
          console.log(error.message || error);

          this.activationError = error.message;

          this.changeDetectorRef.detectChanges();

          return EMPTY;
        }),
      )
      .subscribe();

    Tracking.track(PortalTrackingEvents.portalActivationCodeEntered);
  }

  hideModal() {
    this.popupsService.hideLast();
  }

  showErrorModal() {
    this.popupsService.showModal(this.errorTemplate);
  }

  showSignInModal() {
    this.popupsService.showModal(this.signInTemplate, {
      onHideModal: this.signInModalService.isRedirect
        ? this.onHideSignInModal.bind(this)
        : undefined,
    });
  }

  onHideSignInModal() {
    if (this.signInModalService.isRedirect) {
      this.commonDataService
        .getData()
        .pipe(take(1))
        .subscribe((data) => (window.location.href = data.url));
      return;
    }

    this.hideModal();
  }

  initSessionUser() {
    this.authService
      .hasSession()
      .pipe(
        switchMap((hasSession) => {
          if (hasSession) {
            return this.authService.initSessionUser(null).pipe(handleError(this.errorService));
          }

          this.changeDetectorRef.detectChanges();

          console.error('Invalid session: initSessionUser');
          return EMPTY;
        }),
        switchMap(() => this.getAuthorizedData()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  getAuthorizedData() {
    const variables = { portalLink: this.stateService.getState().portalLink };

    return this.apiProtectedService
      .authorizedData(variables)
      .pipe(map((data) => this.handleAuthorizedData(data)));
  }

  showVerificationModal() {
    this.popupsService.showModal(this.verificationTemplate);
  }

  showPasscodeModal() {
    Tracking.track(PortalTrackingEvents.portalQuotePasscodeScreenShown);
    setTimeout(() =>
      this.popupsService.showModal(this.passcodeTemplate, { closeOnBackdropClick: false }),
    );
  }

  showActivationCodeModal() {
    this.popupsService.showModal(this.activationCodeTemplate);
  }

  trackPasscodeSuccess(role: PortalQuotePasscodeRole | null) {
    if (role === PortalQuotePasscodeRole.AGENT) {
      Tracking.track(PortalTrackingEvents.portalQuotePasscodeAgentSuccess);
      return;
    }

    if (role === PortalQuotePasscodeRole.CUSTOMER) {
      Tracking.track(PortalTrackingEvents.portalQuotePasscodeClientSuccess);
    }
  }

  validatePasscode(passcode: string) {
    this.passcodeModalService.setPasscodePassed(false);
    this.passcodeModalService.setPasscodeError('');

    const portalLink = this.passcodeModalService.portalLink;

    this.apiService
      .unlockPortalQuote({ input: { portalLink, passcode } })
      .pipe(take(1), handleError(this.errorService))
      .subscribe(({ ok, error, token, role }) => {
        if (!ok) {
          Tracking.track(PortalTrackingEvents.portalQuotePasscodeInvalidCode);
          this.passcodeModalService.setPasscodeError(error || 'Invalid passcode');
          this.changeDetectorRef.detectChanges();
          return;
        }

        if (token) {
          this.passcodeTokenService.setToken(portalLink, token);
        }

        this.passcodeModalService.setPasscodePassed(true);
        this.trackPasscodeSuccess(role as PortalQuotePasscodeRole | null);
        this.passcodeModalService.unlock();
        this.changeDetectorRef.detectChanges();
      });
  }

  portalSendVerificationEmail() {
    const portalLink = this.verificationModalService.portalLink;

    const variables = { portalLink };

    this.apiProtectedService
      .portalSendVerificationEmail(variables)
      .pipe(take(1), handleError(this.errorService))
      .subscribe(({ result }) => {
        this.hideModal();
        this.handleAuthorizedData(result);

        this.changeDetectorRef.detectChanges();
      });
  }

  validateCode(verificationCode: string) {
    this.verificationModalService.setVerificationPassed(false);
    this.verificationModalService.setVerificationError('');

    const portalLink = this.verificationModalService.portalLink;

    const variables = { portalLink, verificationCode };

    this.apiProtectedService
      .portalAcceptVerification(variables)
      .pipe(take(1), handleError(this.errorService))
      .subscribe(({ data, errors }) => {
        if (errors?.length) {
          this.verificationModalService.setVerificationError(errors[0].message);
          this.changeDetectorRef.detectChanges();
        } else {
          this.verificationModalService.setVerificationPassed(true);
          this.changeDetectorRef.detectChanges();
          this.handleAuthorizedData(data.portalAcceptVerification.result);
        }
      });
  }

  handleAuthorizedData(data: AuthorizedDataNode | null) {
    if (!data) {
      return;
    }

    this.authorizedDataService.patchAuthorizedData(data);

    if (data.verification?.required) {
      this.verificationModalService.showModal(
        data.verification,
        this.stateService.getState().portalLink,
      );
    } else {
      void this.resumeCheckoutAfterLogin();
    }

    Tracking.patch({ verificationRequired: !!data.verification?.required });
  }

  async resumeCheckoutAfterLogin() {
    for (let attempt = 0; attempt < 3; attempt++) {
      if (await this.checkoutResumeService.resumeAfterLogin()) {
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    void this.router.navigate([], { queryParamsHandling: 'merge' });
  }
}
